<template>
    <div>
        <canvas width="800" height="250"></canvas>

        <sales-development-breakdown-modal
            v-if="monthBreakdown"
            :filters="filters"
            :month="monthBreakdown"
        />
    </div>
</template>

<script>
import datalabels from 'chartjs-plugin-datalabels'
import Chart from 'chart.js'

const totalizer = {
    id: 'totalizer',

    beforeUpdate: chart => {
        let totals = {}
        let utmost = 0

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (chart.isDatasetVisible(datasetIndex)) {
                utmost = datasetIndex

                dataset.data.forEach((value, index) => {
                    totals[index] = (totals[index] || 0) + (datasetIndex > 0 ? value : 0)
                })
            }
        })

        chart.$totalizer = {
            totals: totals,
            utmost: utmost
        }
    }
}

import SalesDevelopmentBreakdownModal from '@/components/Modals/Dashboard/Subscriptions/SalesDevelopmentBreakdown'

export default {
    components: {SalesDevelopmentBreakdownModal},

    props: ['metricType', 'section', 'rows', 'filters', 'loading'],

    mounted() {
        this.$nextTick(this.draw)
    },

    data() {
        return {
            monthBreakdown: null
        }
    },

    watch: {
        loading() {
            this.$nextTick(this.draw)
        }
    },

    computed: {
        totalTargetDataset() {
            return {
                label: 'Target (total)',
                data: this.targetColumn,
                type: 'line',
                backgroundColor: '#888888',
                borderColor: '#888888',
                fill: false,
                stack: 'target'
            }
        },

        newTargetDataset() {
            return {
                label: 'Target (new)',
                data: this.newTargetColumn,
                type: 'line',
                backgroundColor: '#888888',
                borderColor: '#888888',
                fill: false,
                stack: 'new_target'
            }
        },

        renewalTargetDataset() {
            return {
                label: 'Target (renewal)',
                data: this.renewalTargetColumn,
                type: 'line',
                backgroundColor: '#888888',
                borderColor: '#888888',
                fill: false,
                stack: 'renewal_target'
            }
        },

        actualNewDataset() {
            return {
                label: 'New (actual)',
                data: this.actualNewColumn,
                backgroundColor: '#62d26f',
                stack: 'value'
            }
        },

        expectedNewDataset() {
            return {
                label: 'New (expected)',
                data: this.expectedNewColumn,
                backgroundColor: '#37c5ab',
                stack: 'value'
            }
        },

        renewedDataset() {
            return {
                label: 'Renewed',
                data: this.renewedColumn,
                backgroundColor: '#a4cf30',
                stack: 'value'
            }
        },

        endingNonRenewedDataset() {
            return {
                label: 'Expected',
                data: this.endingWithRenewalColumn,
                backgroundColor: '#fd9a00',
                stack: 'value'
            }
        },

        datasets() {
            if (this.section == null || this.section === '') {
                return [
                    this.totalTargetDataset,
                    this.actualNewDataset,
                    this.expectedNewDataset,
                    this.renewedDataset,
                    this.endingNonRenewedDataset
                ]
            }

            if (this.section === 'new') {
                return [
                    this.newTargetDataset,
                    this.actualNewDataset,
                    this.expectedNewDataset,
                ]
            }

            if (this.section === 'renewal') {
                return [
                    this.renewalTargetDataset,
                    this.renewedDataset,
                    this.endingNonRenewedDataset
                ]
            }
        },

        targetColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.target)

                return result
            }, [])
        },

        newTargetColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.new_target)

                return result
            }, [])
        },

        renewalTargetColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.renewal_target)

                return result
            }, [])
        },

        actualNewColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.actual_new)

                return result
            }, [])
        },

        expectedNewColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.expected_new)

                return result
            }, [])
        },

        renewedColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.renewed)

                return result
            }, [])
        },

        endingWithRenewalColumn() {
            return this.rows.reduce((result, row) => {
                result.push(row.ending_with_renewal)

                return result
            }, [])
        },

        labels() {
            return this.rows.map(row => row.month.format('MMMM'))
        }
    },

    methods: {
        draw() {
            new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                plugins: [datalabels, totalizer],

                type: 'bar',

                data: {
                    labels: this.labels,
                    datasets: this.datasets
                },

                options: {
                    scales: {
                        xAxes: [{}],
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: (value, context) => this.$options.filters.price(value)
                            }
                        }]
                    },

                    legend: {
                        position: 'bottom'
                    },

                    layout: {
                        padding: {
                            top: 25
                        }
                    },

                    plugins: {
                        datalabels: {
                            anchor: 'end',
                            align: 'end',
                            formatter: (value, ctx) => this.format(ctx.chart.$totalizer.totals[ctx.dataIndex]),
                            display: (ctx) => ctx.datasetIndex === ctx.chart.$totalizer.utmost
                        }
                    },

                    tooltips: {
                        callbacks: {
                            label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + this.format(d.datasets[t.datasetIndex].data[t.index])
                        }
                    },

                    onClick: (event, elements) => {
                        if (elements.length) {
                            this.monthBreakdown = this.rows[elements[0]['_index']].month.format('YYYY-MM-DD')

                            this.$nextTick(() => $('#show-sales-development-breakdown-modal').modal('show'))
                        }
                    }
                }
            })
        },

        format(value) {
            if (this.metricType === 'revenue') {
                return this.$options.filters.price(value)
            }

            return this.$options.filters.number(value)
        }
    }
}
</script>
